import { useEffect, useState, useMemo } from "react";
import { useNavigate, useOutletContext } from "react-router-dom";
import { TextButton, LoadingCircle } from "@industriousoffice/react-components";
import { useTranslation } from "react-i18next";
import { opbApi } from "@/api/opb/opbApi";
import InvoiceTableCard from "@/components/InvoiceTableCard";
import { getFullPath } from "@/router/utils/getFullPath";
import { slugs } from "@/router/slugs";
import Layout from "@/components/Layout";
import * as Sentry from "@sentry/react";

import { isApiError } from "@/api/utils/isApiError";
import { ErrorMessage } from "@/components/ErrorMessage/ErrorMessage";
import { PageOutletContext } from "@/components/Page/PageOutlet";

import { authApi } from "../../api/auth/authApi";
import {
  Account,
  Invoice,
  InvoicingSystemTypes,
  Location,
  Order,
  SecurityDepositRefundStatus,
} from "../../api/opb/opbTypes";
import { PageTitle } from "../../components/PageTitle";
import { NextPaymentDue } from "./components/NextPaymentDue";
import { CurrentBalance } from "./components/CurrentBalance";
import { InvoiceTableFooter } from "./components/InvoiceTableFooter";
import InvoiceAccountName from "./components/InvoiceAccountName";
import { SecurityDepositBanner } from "./components/SecurityDepositBanner";
import { IncorrectBankInfoBanner } from "./components/IncorrectBankInfoBanner";
import { AccountActivationBanner } from "./components/AccountActivationBanner";
import { VerificationRequiredBanner } from "./components/VerificationRequiredBanner";

type CurrencyInvoiceMap = Record<
  string,
  { invoices: Invoice[]; numLocations: number }
>;

export function Dashboard() {
  const [accounts, setAccounts] = useState<Account[]>([]);
  const [locationMap, setLocationMap] = useState<
    Record<string, Location> | undefined
  >();

  const [accountInvoiceMap, setAccountInvoiceMap] = useState<
    Record<string, Invoice[]>
  >({});
  const [accountInvoiceErrorMap, setAccountInvoiceErrorMap] = useState<
    Record<string, Error>
  >({});
  const [accountOrdersMap, setAccountOrdersMap] = useState<
    Record<string, Order[]>
  >({});
  const [isLoadingInvoices, setIsLoadingInvoices] = useState<boolean>(true);
  const [isFetchingAccounts, setIsFetchingAccounts] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const { currentUser } = useOutletContext<PageOutletContext>();

  const navigate = useNavigate();
  const { t } = useTranslation("dashboard");

  if (error) {
    // asynchronous errors happening are not caught by the ErrorBoundary
    // we need to explicitly throw them (and then the ErrorBoundary page will be displayed - and the error will be sent to Sentry)
    throw error;
  }

  const fetchUserAccounts = async () => {
    try {
      setIsFetchingAccounts(true);
      const token = await authApi.getCognitoUser();
      const userId = token["custom:user_id"];
      const userAccounts = await opbApi.fetchUserAccounts(userId);
      setAccounts(userAccounts.data);
    } catch (e) {
      setError(new Error(t("Error when fetching user accounts"), { cause: e }));
      // eslint-disable-next-line no-console
      console.error(e);
    } finally {
      setIsFetchingAccounts(false);
    }
  };

  const fetchAccountInvoices = async () => {
    try {
      setIsLoadingInvoices(true);
      const newInvoices = {} as Record<string, Invoice[]>;
      const nextAccountInvoiceErrorMap: Record<string, Error> = {};

      await Promise.all(
        accounts.map(async (a) => {
          try {
            const invoicesResponse = await opbApi.fetchAccountInvoices(a._id, {
              withBalance: false,
              isStripeBilling:
                a.invoicingSystemType === InvoicingSystemTypes.StripeBilling,
            });
            newInvoices[a._id] = invoicesResponse;
          } catch (e) {
            if (e instanceof Error) {
              const cause = isApiError(e)
                ? e.response?.data?.error?.message || e.message
                : e.message;
              nextAccountInvoiceErrorMap[a._id] = new Error(
                t(
                  "We’re sorry, something went wrong. Please reach out to your MXM to resolve this issue"
                ),
                {
                  cause,
                }
              );
            }
            Sentry.captureException(error);
          }
        })
      );
      setAccountInvoiceMap(newInvoices);
      setAccountInvoiceErrorMap(nextAccountInvoiceErrorMap);
      setIsLoadingInvoices(false);
    } catch (e) {
      setIsLoadingInvoices(false);
      setAccountInvoiceMap({});
    }
  };

  const fetchUserOrders = async () => {
    const { data: orders } = await opbApi.fetchCurrentUserOrders();
    const ordersByAccountSfId = orders.reduce((acc, order) => {
      if (!acc[order.account.sfId]) {
        // eslint-disable-next-line no-param-reassign
        acc[order.account.sfId] = [];
      }
      acc[order.account.sfId].push(order);
      return acc;
    }, {} as Record<string, Order[]>);
    setAccountOrdersMap(ordersByAccountSfId);
  };

  useEffect(() => {
    fetchUserAccounts();
    fetchUserOrders();
  }, []);

  useEffect(() => {
    if (!accounts.length) return;
    if (locationMap) return;
    opbApi
      .fetchLocations({
        locationIds: accounts
          .map((a) => a.locationId)
          .filter((id) => id != null),
      })
      .then((data) => {
        setLocationMap(Object.fromEntries(data.map((d) => [d._id, d])));
      });
  }, [accounts.length]);

  useEffect(() => {
    if (!accounts?.length) return;
    fetchAccountInvoices();
  }, [accounts.length]);

  // if an account only has one invoice
  // we consider it as a new account that needs to be activated
  // (or has been activated)
  const newAccount = accounts.find(
    (a) => accountInvoiceMap[a._id]?.length === 1
  );
  const newAccountActivated = newAccount && newAccount.balance === 0;
  const newAccountOrder = newAccount && accountOrdersMap[newAccount.sfId]?.[0];

  const accountGlobalNames = accounts
    .map((account) => account.globalAccountName)
    .join(" | ");

  const invoices = Object.values(accountInvoiceMap).flat();

  const currencyInvoiceMap: CurrencyInvoiceMap = useMemo(
    () =>
      accounts.reduce((currencyMap, account) => {
        if (!accountInvoiceMap[account._id]?.length) {
          return currencyMap;
        }
        return {
          ...currencyMap,
          [account.currencyCode]: {
            invoices: [
              ...(currencyMap[account.currencyCode]?.invoices ?? []),
              ...(accountInvoiceMap[account._id] ?? []),
            ],
            numLocations:
              (currencyMap[account.currencyCode]?.numLocations ?? 0) + 1,
          },
        };
      }, {} as CurrencyInvoiceMap),
    [accounts, accountInvoiceMap]
  );

  if (isFetchingAccounts) {
    return (
      <div className="flex items-center justify-center mt-10">
        <LoadingCircle spinnerColor="#013e3f" />
      </div>
    );
  }

  const navigateToPaymentsMethod = () => {
    navigate(getFullPath(slugs.paymentMethods));
  };

  const primaryAccount = accounts.find(
    (a) => a._id === currentUser.primaryAccount
  );
  return (
    <Layout className="overflow-hidden mb-9">
      <VerificationRequiredBanner timezone={primaryAccount?.timezone} />
      <PageTitle data-testid="dashboard-title">
        {t("Billing Management")}
      </PageTitle>
      <div
        className={`items-center lg:justify-between ${
          newAccount ? "[&>p]:pb-2" : "lg:flex"
        }`}
      >
        <p className="inline-block text-base font-semibold font-hanken">
          {accountGlobalNames}
        </p>
        <div className="flex items-center justify-center mt-4 lg:justify-start lg:mt-0">
          {newAccountOrder && (
            <div className="">
              <a
                href={new URL(
                  `/proposals/${newAccountOrder?.proposalId}/agreement`,
                  import.meta.env.VITE_WEBSITE_URL
                ).toString()}
                target="_blank"
                rel="noreferrer"
              >
                <TextButton
                  className="text-base! font-bold hover:bg-teal-99 rounded-full px-2"
                  data-testid="dashboard-view_agrrement-btn"
                >
                  {t("View Agreement")}
                </TextButton>
              </a>
              <a
                href={new URL(
                  `/proposals/${newAccountOrder?.proposalId}`,
                  import.meta.env.VITE_WEBSITE_URL
                ).toString()}
                target="_blank"
                rel="noreferrer"
              >
                <TextButton
                  className="text-base! font-bold hover:bg-teal-99 rounded-full px-2"
                  data-testid="dashboard-view_proposal-btn"
                >
                  {t("View Proposal")}
                </TextButton>
              </a>
            </div>
          )}
          <TextButton
            className="text-base! font-bold hover:bg-teal-99 rounded-full px-2 ml-auto"
            onClick={navigateToPaymentsMethod}
            data-testid="dashboard-manage_payment_methods-btn"
          >
            {t("Payment Methods & Preferences")}
          </TextButton>
        </div>
      </div>
      {newAccount && (
        <AccountActivationBanner
          isActivated={!!newAccountActivated}
          account={newAccount}
          className="mt-4"
        />
      )}
      {accounts
        .filter(
          (a) =>
            a.securityDeposit?.refundStatus ===
            SecurityDepositRefundStatus.IncorrectBankInfo
        )
        .map((account) => (
          <IncorrectBankInfoBanner
            account={account}
            key={account._id}
            className="mt-4"
          />
        ))}
      {accounts
        .filter(
          (account) =>
            account.moveOutDate &&
            account.securityDeposit?.refundInfoRequestEmailTriggeredAt &&
            (account.securityDeposit?.refundStatus == null ||
              account.securityDeposit.refundStatus ===
                SecurityDepositRefundStatus.Open)
        )
        .map((account) => (
          <SecurityDepositBanner
            account={account}
            location={locationMap?.[account.locationId]}
            className="mt-4"
          />
        ))}
      <div className="flex px-1 py-1 overflow-x-auto my-9">
        {Object.keys(currencyInvoiceMap).map((currency) => (
          <div className="mr-5" key={currency}>
            <CurrentBalance
              invoices={currencyInvoiceMap[currency].invoices}
              numLocations={currencyInvoiceMap[currency].numLocations}
              isLoading={isLoadingInvoices}
              currency={currency}
            />
          </div>
        ))}
        <div className="mr-1">
          <NextPaymentDue invoices={invoices} isLoading={isLoadingInvoices} />
        </div>
      </div>

      <div>
        {accounts.map((account) => {
          const location = locationMap?.[account.locationId];

          const name = location
            ? `${location.city} — ${location.name}`
            : account.globalAccountName;

          const accountUnpaidInvoices =
            accountInvoiceMap[account._id]?.filter((i) => i.balance > 0) ?? [];

          const invoiceError = accountInvoiceErrorMap[account._id];

          return (
            <div className="mt-9" key={account._id}>
              <InvoiceAccountName
                name={name}
                isAutoPayOn={account.autopay}
                hasDefaultPaymentMethod={Boolean(
                  account.defaultPaymentMethodId
                )}
              />
              {invoiceError ? (
                <ErrorMessage error={invoiceError} />
              ) : (
                <InvoiceTableCard
                  invoices={accountUnpaidInvoices}
                  isLoading={isLoadingInvoices}
                  description={account.name}
                  currencyCode={account.currencyCode}
                  timezone={account.timezone}
                  withAction
                  actionText={t("View Invoice")}
                  onAction={(invoiceId) =>
                    navigate(
                      getFullPath(slugs.invoice, {
                        invoiceId,
                        accountId: account._id,
                      })
                    )
                  }
                  footer={
                    <InvoiceTableFooter
                      account={account}
                      accountCurrencyCode={account.currencyCode}
                      invoices={accountUnpaidInvoices}
                      isLoading={isLoadingInvoices}
                    />
                  }
                />
              )}
            </div>
          );
        })}
      </div>
    </Layout>
  );
}
